/* eslint-disable max-lines -- Cohesive dev-only UI section (toolbar, filters, row list, diff view). */
import { useAtom, useAtomValue } from 'jotai';
import {
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  Copy,
  Pause,
  Play,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/cn';
import {
  analyticsInspectorDestinationsAtom,
  analyticsInspectorEventsAtom,
  analyticsInspectorFiltersAtom,
  analyticsInspectorPausedAtom,
  clearAnalyticsInspectorEvents,
} from '@/utils/analytics/inspector/store';
import type {
  AnalyticsInspectorDestination,
  AnalyticsInspectorEvent,
} from '@/utils/analytics/inspector/types';
import { subscribeToWidgetAnalyticsEvents } from '@/utils/analytics/inspector/widget-bridge';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { SearchInput } from '@heidi/design-system/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';

const TEST_IDS = {
  root: 'analytics-inspector-section',
  pause: 'analytics-inspector-pause',
  clear: 'analytics-inspector-clear',
  search: 'analytics-inspector-search',
  destinationFilter: 'analytics-inspector-destination-filter',
  eventRow: 'analytics-inspector-event-row',
  eventEmpty: 'analytics-inspector-empty',
} as const;

function formatTime(timestamp: number): string {
  const d = new Date(timestamp);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  const ss = d.getSeconds().toString().padStart(2, '0');
  const ms = d.getMilliseconds().toString().padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

/**
 * Resolved filter state: returns the effective destinations enabled for the
 * current event log, taking `null` to mean "all destinations".
 */
function resolveEnabledDestinations(
  filterEnabled: string[] | null,
  allDestinations: string[]
): Set<string> {
  if (filterEnabled === null) return new Set(allDestinations);
  return new Set(filterEnabled);
}

function eventMatchesSearch(event: AnalyticsInspectorEvent, query: string): boolean {
  if (!query) return true;
  const needle = query.toLowerCase();
  if (event.eventName.toLowerCase().includes(needle)) return true;
  try {
    return JSON.stringify(event.destinations).toLowerCase().includes(needle);
  } catch {
    return false;
  }
}

export function AnalyticsInspectorDevSection() {
  const events = useAtomValue(analyticsInspectorEventsAtom);
  const allDestinations = useAtomValue(analyticsInspectorDestinationsAtom);
  const [paused, setPaused] = useAtom(analyticsInspectorPausedAtom);
  const [filters, setFilters] = useAtom(analyticsInspectorFiltersAtom);

  // Replay dictation-widget events (separate webview) into this inspector.
  useEffect(() => subscribeToWidgetAnalyticsEvents(), []);

  const enabled = useMemo(
    () => resolveEnabledDestinations(filters.enabledDestinations, allDestinations),
    [filters.enabledDestinations, allDestinations]
  );

  const visibleEvents = useMemo(() => {
    return events.filter((event) => {
      const matchesDest =
        event.destinations.length === 0 ||
        event.destinations.some((destination) => enabled.has(destination.destination));
      return matchesDest && eventMatchesSearch(event, filters.search);
    });
  }, [events, enabled, filters.search]);

  const toggleDestination = useCallback(
    (destination: string) => {
      const current =
        filters.enabledDestinations === null
          ? [...allDestinations]
          : [...filters.enabledDestinations];
      const has = current.includes(destination);
      const next = has
        ? current.filter((d) => d !== destination)
        : [...current, destination];
      setFilters({
        ...filters,
        // Collapse "everything enabled" back to `null` so newly observed
        // destinations don't get auto-excluded.
        enabledDestinations:
          next.length === allDestinations.length &&
          allDestinations.every((d) => next.includes(d))
            ? null
            : next,
      });
    },
    [allDestinations, filters, setFilters]
  );

  const enableAllDestinations = useCallback(() => {
    setFilters({ ...filters, enabledDestinations: null });
  }, [filters, setFilters]);

  const onSearchChange = useCallback(
    (value: string) => {
      setFilters({ ...filters, search: value });
    },
    [filters, setFilters]
  );

  const onClear = useCallback(() => {
    clearAnalyticsInspectorEvents();
  }, []);

  return (
    <section
      aria-labelledby="dev-tools-analytics-heading"
      data-testid={TEST_IDS.root}
      className="flex min-h-0 flex-1 flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2">
        <TypographyP2SemiBold id="dev-tools-analytics-heading">
          Analytics Inspector
        </TypographyP2SemiBold>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setPaused(!paused)}
            data-testid={TEST_IDS.pause}
            title={paused ? 'Resume capturing events' : 'Pause capturing events'}
          >
            {paused ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
            {paused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={onClear}
            disabled={events.length === 0}
            data-testid={TEST_IDS.clear}
            title="Clear all captured events"
          >
            <Trash2 size={14} aria-hidden />
            Clear
          </Button>
        </div>
      </div>

      <TypographyCaption className="text-text-tertiary">
        Captures every <code className="font-mono">trackEventV2</code> dispatch with its
        per-destination payload. Persists across page reloads (sessionStorage, last{' '}
        {events.length > 0 ? events.length : 0} of 200). Available in all non-production
        environments.
      </TypographyCaption>

      <SearchInput
        value={filters.search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search by event name or any prop value"
        aria-label="Search captured analytics events"
        clearAriaLabel="Clear search"
        data-testid={TEST_IDS.search}
      />

      <DestinationFilterRow
        destinations={allDestinations}
        enabled={enabled}
        onToggle={toggleDestination}
        onEnableAll={enableAllDestinations}
        allEnabled={filters.enabledDestinations === null}
      />

      <div className="-mr-2 mt-1 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-2">
        {visibleEvents.length === 0 ? (
          <EmptyState
            hasEvents={events.length > 0}
            hasFilter={filters.search.length > 0 || filters.enabledDestinations !== null}
          />
        ) : (
          visibleEvents.map((event) => (
            <AnalyticsInspectorEventRow key={event.id} event={event} />
          ))
        )}
      </div>
    </section>
  );
}

function EmptyState({
  hasEvents,
  hasFilter,
}: {
  hasEvents: boolean;
  hasFilter: boolean;
}) {
  if (hasEvents && hasFilter) {
    return (
      <div
        data-testid={TEST_IDS.eventEmpty}
        className="rounded-md border border-dashed border-border p-4 text-center"
      >
        <TypographyP2 className="text-text-secondary">
          No events match the current filters.
        </TypographyP2>
      </div>
    );
  }
  return (
    <div
      data-testid={TEST_IDS.eventEmpty}
      className="rounded-md border border-dashed border-border p-4 text-center"
    >
      <TypographyP2 className="text-text-secondary">
        No events captured yet. Interact with the app to populate the log.
      </TypographyP2>
    </div>
  );
}

function DestinationFilterRow({
  destinations,
  enabled,
  onToggle,
  onEnableAll,
  allEnabled,
}: {
  destinations: string[];
  enabled: Set<string>;
  onToggle: (destination: string) => void;
  onEnableAll: () => void;
  allEnabled: boolean;
}) {
  if (destinations.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <TypographyCaption className="text-text-tertiary">Destinations:</TypographyCaption>
      {destinations.map((destination) => {
        const isOn = enabled.has(destination);
        return (
          <button
            key={destination}
            type="button"
            onClick={() => onToggle(destination)}
            data-testid={`${TEST_IDS.destinationFilter}-${destination}`}
            aria-pressed={isOn}
            className={cn(
              'rounded-full border px-2 py-0.5 text-xs font-medium transition-colors',
              isOn
                ? 'border-validation-success bg-validation-success/10 text-validation-success'
                : 'border-border bg-surface text-text-tertiary hover:bg-surface-elevated'
            )}
          >
            {destination}
          </button>
        );
      })}
      {!allEnabled ? (
        <Button type="button" variant="ghost" size="xxs" onClick={onEnableAll}>
          Enable all
        </Button>
      ) : null}
    </div>
  );
}

const DESTINATION_BADGE_COLOR: Record<
  string,
  'green' | 'blue' | 'purple' | 'yellow' | 'red' | 'neutral'
> = {
  snowplow: 'purple',
  amplitude: 'blue',
  datadog: 'yellow',
  posthog: 'green',
  segment: 'red',
};

function destinationColor(name: string) {
  return DESTINATION_BADGE_COLOR[name] ?? 'neutral';
}

function AnalyticsInspectorEventRow({ event }: { event: AnalyticsInspectorEvent }) {
  const [expanded, setExpanded] = useState(false);
  const [diffMode, setDiffMode] = useState(false);
  const initialA = event.destinations[0]?.destination ?? '';
  const initialB = event.destinations[1]?.destination ?? initialA;
  const [destA, setDestA] = useState(initialA);
  const [destB, setDestB] = useState(initialB);

  const canDiff = event.destinations.length >= 2;

  return (
    <article
      data-testid={TEST_IDS.eventRow}
      data-event-name={event.eventName}
      className="rounded-md border border-border bg-surface"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2 p-2 text-left hover:bg-surface-elevated"
        aria-expanded={expanded}
      >
        <span className="mt-0.5 shrink-0 text-text-tertiary">
          {expanded ? (
            <ChevronDown size={14} aria-hidden />
          ) : (
            <ChevronRight size={14} aria-hidden className="rtl:-scale-x-100" />
          )}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-baseline gap-2">
            <code className="truncate font-mono text-xs text-text-secondary">
              {formatTime(event.timestamp)}
            </code>
            <code className="truncate font-mono text-sm font-semibold">
              {event.eventName}
            </code>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {event.destinations.map((destination) => (
              <Badge
                key={destination.destination}
                size="xs"
                color={destinationColor(destination.destination)}
                className="font-normal"
              >
                {destination.destination}
              </Badge>
            ))}
          </div>
          <code
            className={cn(
              'truncate font-mono text-xs',
              event.amplitudeUserId ? 'text-text-tertiary' : 'text-validation-warning'
            )}
          >
            amp user_id: {event.amplitudeUserId ?? 'anonymous'}
          </code>
        </div>
      </button>

      {expanded ? (
        <div className="border-t border-border p-2">
          <div className="mb-2 flex items-center justify-between gap-2">
            <TypographyCaption className="text-text-tertiary">
              {event.destinations.length} destination
              {event.destinations.length === 1 ? '' : 's'}
            </TypographyCaption>
            {canDiff ? (
              <Button
                type="button"
                variant="outline"
                size="xxs"
                onClick={() => setDiffMode((v) => !v)}
              >
                <ArrowLeftRight size={12} aria-hidden />
                {diffMode ? 'Single view' : 'Compare destinations'}
              </Button>
            ) : null}
          </div>

          {diffMode && canDiff ? (
            <DiffView
              event={event}
              destA={destA}
              destB={destB}
              onChangeA={setDestA}
              onChangeB={setDestB}
            />
          ) : (
            <DestinationTabs event={event} />
          )}
        </div>
      ) : null}
    </article>
  );
}

function DestinationTabs({ event }: { event: AnalyticsInspectorEvent }) {
  const defaultValue = event.destinations[0]?.destination ?? '';
  if (event.destinations.length === 0) {
    return (
      <TypographyCaption className="text-text-tertiary">
        No destinations recorded.
      </TypographyCaption>
    );
  }
  return (
    <Tabs defaultValue={defaultValue}>
      <TabsList variant="line" className="w-max min-w-full justify-start">
        {event.destinations.map((destination) => (
          <TabsTrigger key={destination.destination} value={destination.destination}>
            {destination.destination}
          </TabsTrigger>
        ))}
      </TabsList>
      {event.destinations.map((destination) => (
        <TabsContent
          key={destination.destination}
          value={destination.destination}
          className="mt-2"
        >
          <PayloadBlock destination={destination} />
        </TabsContent>
      ))}
    </Tabs>
  );
}

function PayloadBlock({ destination }: { destination: AnalyticsInspectorDestination }) {
  const [copied, setCopied] = useState(false);
  const json = useMemo(() => {
    try {
      return JSON.stringify(destination.props, null, 2);
    } catch {
      return '/* unserialisable */';
    }
  }, [destination.props]);

  const onCopy = useCallback(() => {
    void navigator.clipboard?.writeText(json).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  }, [json]);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="xxs"
        className="absolute right-1 top-1"
        onClick={onCopy}
        aria-label={`Copy ${destination.destination} payload as JSON`}
      >
        <Copy size={12} aria-hidden />
        {copied ? 'Copied' : 'Copy'}
      </Button>
      <pre className="max-h-96 overflow-auto rounded-md bg-surface-elevated p-2 pr-16 font-mono text-xs">
        {json}
      </pre>
    </div>
  );
}

function DiffView({
  event,
  destA,
  destB,
  onChangeA,
  onChangeB,
}: {
  event: AnalyticsInspectorEvent;
  destA: string;
  destB: string;
  onChangeA: (value: string) => void;
  onChangeB: (value: string) => void;
}) {
  const a = event.destinations.find((d) => d.destination === destA);
  const b = event.destinations.find((d) => d.destination === destB);

  return (
    <div className="grid grid-cols-2 gap-2">
      <DiffColumn
        destinations={event.destinations}
        selected={destA}
        onSelect={onChangeA}
        otherProps={b?.props}
        currentProps={a?.props}
      />
      <DiffColumn
        destinations={event.destinations}
        selected={destB}
        onSelect={onChangeB}
        otherProps={a?.props}
        currentProps={b?.props}
      />
    </div>
  );
}

function DiffColumn({
  destinations,
  selected,
  onSelect,
  otherProps,
  currentProps,
}: {
  destinations: AnalyticsInspectorDestination[];
  selected: string;
  onSelect: (value: string) => void;
  otherProps: Record<string, unknown> | undefined;
  currentProps: Record<string, unknown> | undefined;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Select value={selected} onValueChange={onSelect}>
        <SelectTrigger
          aria-label="Select destination to compare"
          className="h-7 rounded px-2 py-1 font-mono text-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {destinations.map((destination) => (
            <SelectItem key={destination.destination} value={destination.destination}>
              {destination.destination}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DiffPayload currentProps={currentProps ?? {}} otherProps={otherProps ?? {}} />
    </div>
  );
}

function DiffPayload({
  currentProps,
  otherProps,
}: {
  currentProps: Record<string, unknown>;
  otherProps: Record<string, unknown>;
}) {
  const allKeys = useMemo(() => {
    const set = new Set<string>([
      ...Object.keys(currentProps),
      ...Object.keys(otherProps),
    ]);
    return [...set].sort();
  }, [currentProps, otherProps]);

  return (
    <pre className="max-h-96 overflow-auto rounded-md bg-surface-elevated p-2 font-mono text-xs">
      {allKeys.map((key) => {
        const hasHere = key in currentProps;
        const valueHere = currentProps[key];
        const valueOther = otherProps[key];
        const differs =
          !hasHere ||
          !(key in otherProps) ||
          JSON.stringify(valueHere) !== JSON.stringify(valueOther);
        return (
          <div
            key={key}
            className={cn(
              'whitespace-pre-wrap break-words',
              differs && hasHere && 'bg-validation-warning/10',
              !hasHere && 'text-text-tertiary line-through'
            )}
          >
            <span className="text-text-secondary">{key}</span>
            <span>: </span>
            <span>{hasHere ? JSON.stringify(valueHere) : '<absent>'}</span>
          </div>
        );
      })}
    </pre>
  );
}
